import { useTranslation } from 'react-i18next';
import { Download, X, Share } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';

export function InstallPrompt() {
  const { t } = useTranslation();
  const { showPrompt, platform, install, dismiss } = useInstallPrompt();

  const renderInstructions = () => {
    if (platform === 'ios') {
      return (
        <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
          {t('App.PWA.InstallPrompt.IosTapShare')}{' '}
          <Share className="inline w-4 h-4 -mt-0.5 text-primary-600" />{' '}
          {t('App.PWA.InstallPrompt.IosThen')}{' '}
          <strong>{t('App.PWA.InstallPrompt.IosAddToHomeScreen')}</strong>
        </p>
      );
    }

    if (platform === 'macos-safari') {
      return (
        <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
          {t('App.PWA.InstallPrompt.MacSafariInstructions')}
        </p>
      );
    }

    return (
      <>
        <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
          {t('App.PWA.InstallPrompt.Description')}
        </p>
        <div className="mt-3 flex gap-2">
          <button
            onClick={install}
            className="px-4 py-2 bg-secondary-600 hover:bg-secondary-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {t('App.PWA.InstallPrompt.Install')}
          </button>
          <button
            onClick={dismiss}
            className="px-4 py-2 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300 text-sm font-medium rounded-lg transition-colors"
          >
            {t('App.PWA.InstallPrompt.NotNow')}
          </button>
        </div>
      </>
    );
  };

  return (
    <AnimatePresence>
      {showPrompt && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 z-[9998] sm:max-w-md"
        >
          <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-secondary-100 dark:bg-secondary-900/30 rounded-full flex items-center justify-center">
                  <Download className="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                  {t('App.PWA.InstallPrompt.Title')}
                </h3>
                {renderInstructions()}
              </div>

              <button
                onClick={dismiss}
                className="flex-shrink-0 p-1 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-full transition-colors"
                aria-label={t('App.PWA.InstallPrompt.DismissAriaLabel')}
              >
                <X className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
